import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFolder, FiFolderPlus, FiChevronRight, FiX, FiBox } from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';

const FolderView = () => {
  const { state, dispatch } = useSolution();
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [selectedSubLayer, setSelectedSubLayer] = useState(null);
  
  // Group data
  const phases = {};
  state.selectedComponents.forEach(comp => {
    if (!phases[comp.phaseId]) {
      phases[comp.phaseId] = {
        name: comp.phaseName,
        icon: comp.phaseIcon || '📦',
        layers: {}
      };
    }
    if (!phases[comp.phaseId].layers[comp.layerId]) {
      phases[comp.phaseId].layers[comp.layerId] = {
        name: comp.layerName,
        subLayers: {}
      };
    }
    if (!phases[comp.phaseId].layers[comp.layerId].subLayers[comp.subLayerId]) {
      phases[comp.phaseId].layers[comp.layerId].subLayers[comp.subLayerId] = {
        name: comp.subLayerName,
        components: []
      };
    }
    phases[comp.phaseId].layers[comp.layerId].subLayers[comp.subLayerId].components.push(comp);
  });
  
  return (
    <div className="h-full flex bg-gray-50">
      {/* Breadcrumb Navigation */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6 text-sm">
          <button
            onClick={() => { setSelectedPhase(null); setSelectedLayer(null); setSelectedSubLayer(null); }}
            className="text-blue-600 hover:underline"
          >
            All Phases
          </button>
          {selectedPhase && (
            <>
              <FiChevronRight className="text-gray-400" />
              <button
                onClick={() => { setSelectedLayer(null); setSelectedSubLayer(null); }}
                className="text-blue-600 hover:underline"
              >
                {phases[selectedPhase]?.name}
              </button>
            </>
          )}
          {selectedLayer && (
            <>
              <FiChevronRight className="text-gray-400" />
              <button
                onClick={() => setSelectedSubLayer(null)}
                className="text-blue-600 hover:underline"
              >
                {phases[selectedPhase]?.layers[selectedLayer]?.name}
              </button>
            </>
          )}
          {selectedSubLayer && (
            <>
              <FiChevronRight className="text-gray-400" />
              <span className="text-gray-600">
                {phases[selectedPhase]?.layers[selectedLayer]?.subLayers[selectedSubLayer]?.name}
              </span>
            </>
          )}
        </div>
        
        {/* Phase Cards */}
        {!selectedPhase && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(phases).map(([phaseId, phase], i) => (
              <motion.button
                key={phaseId}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => setSelectedPhase(phaseId)}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg hover:border-blue-400 transition-all text-center"
              >
                <div className="text-4xl mb-3">{phase.icon}</div>
                <div className="font-semibold text-gray-900">{phase.name}</div>
                <div className="text-sm text-gray-500">
                  {Object.keys(phase.layers).length} layers
                </div>
              </motion.button>
            ))}
          </div>
        )}
        
        {/* Layer Cards */}
        {selectedPhase && !selectedLayer && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(phases[selectedPhase].layers).map(([layerId, layer], i) => (
              <motion.button
                key={layerId}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => setSelectedLayer(layerId)}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg hover:border-yellow-400 transition-all"
              >
                <FiFolder className="w-8 h-8 text-yellow-500 mx-auto mb-3" />
                <div className="font-semibold text-gray-900">{layer.name}</div>
                <div className="text-sm text-gray-500">
                  {Object.keys(layer.subLayers).length} sub-layers
                </div>
              </motion.button>
            ))}
          </div>
        )}
        
        {/* Sub-Layer Cards */}
        {selectedLayer && !selectedSubLayer && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(phases[selectedPhase].layers[selectedLayer].subLayers).map(([subLayerId, subLayer], i) => (
              <motion.button
                key={subLayerId}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => setSelectedSubLayer(subLayerId)}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg hover:border-pink-400 transition-all"
              >
                <FiFolderPlus className="w-8 h-8 text-pink-500 mx-auto mb-3" />
                <div className="font-semibold text-gray-900">{subLayer.name}</div>
                <div className="text-sm text-gray-500">
                  {subLayer.components.length} components
                </div>
              </motion.button>
            ))}
          </div>
        )}
        
        {/* Component Cards */}
        {selectedSubLayer && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {phases[selectedPhase].layers[selectedLayer].subLayers[selectedSubLayer].components.map((comp, i) => (
              <motion.div
                key={comp.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-lg"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{comp.icon}</span>
                    <div>
                      <div className="font-semibold text-gray-900">{comp.name}</div>
                      <div className="text-sm text-gray-500">{comp.purpose}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => dispatch({ type: 'REMOVE_COMPONENT', payload: comp })}
                    className="p-1 hover:bg-red-100 rounded"
                  >
                    <FiX className="w-4 h-4 text-red-500" />
                  </button>
                </div>
                <div className="mt-3 flex justify-between text-sm">
                  <span className="text-green-600">${comp.cost?.low || 0} - ${comp.cost?.high || 0}</span>
                  <button
                    onClick={() => dispatch({ type: 'SHOW_COMPONENT_DETAIL', payload: comp })}
                    className="text-blue-600 hover:underline"
                  >
                    Details
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FolderView;